import React from 'react';
import { Level } from '@tiptap/extension-heading';
import { Tooltip } from '@crystaldelta/loree-ui-components';
import { HeaderContentType } from './lintEditorType';
import { EditorMenuButton, HeaderParagraphMenu, HeadingParagraphButton } from './styledContents';
import { ReactComponent as HeadingButton } from '../editorFormattingIcons/headingButton.svg';
import {
  parseFontSize,
  setDefaultFontStyle,
  updateFontStyle,
} from '../editorExtensions/utilityFunctions';
import { IconDiv } from './utilityFunctions';

export function HeadingFormatButton(props: HeaderContentType) {
  const { editor, editorConfig, setEnableIcon, setIsItalicButtonActive } = props;

  if (!editor) {
    return null;
  }

  const setHeading = (level: Level | 0) => {
    const { from, to } = editor.state.selection;
    const { tr, doc } = editor.state;
    if (setIsItalicButtonActive) setIsItalicButtonActive(false);

    let adminDashboardStyles;
    if (editorConfig?.customHeaderStyleList.length) {
      adminDashboardStyles = setDefaultFontStyle(editorConfig.customHeaderStyleList, level);
    }

    let styles = '';
    let fontStyle = 'normal';
    let fontNameWithsttyle = '';

    if (adminDashboardStyles) {
      const customFontSize = parseFontSize(adminDashboardStyles.size);
      styles = `font-family: ${adminDashboardStyles.font}; font-size: ${
        customFontSize?.size ?? '16'
      }${customFontSize?.unit ?? 'px'}; font-style: ${
        adminDashboardStyles.fontStyle ?? 'normal'
      }; `;
      fontNameWithsttyle = adminDashboardStyles.font;
      fontStyle = adminDashboardStyles.fontStyle ?? 'normal';
    }
    const isItalic = fontStyle === 'italic';
    const nodes: Array<{ node: any; pos: number }> = [];

    doc.nodesBetween(from, to, (node, pos) => {
      const parent = doc.resolve(pos).parent;

      if (
        node.type.name === 'paragraph' ||
        node.type.name === 'heading' ||
        (node.type.name === 'paragraph' && parent.type.name === 'listItem')
      ) {
        nodes.push({ node, pos });
      }
    });
    nodes.forEach(({ node, pos }) => {
      if (level === 0) {
        tr.setNodeMarkup(pos, editor.schema.nodes.paragraph, {
          ...node.attrs,
          style: styles,
        });
      } else {
        tr.setNodeMarkup(pos, editor.schema.nodes.heading, {
          ...node.attrs,
          level,
          style: styles,
        });
      }
    });
    editor.view.dispatch(tr);
    if (isItalic) {
      const updatedDoc = editor.state.doc;

      updatedDoc.nodesBetween(from, to, (node, pos) => {
        if (node.isTextblock) {
          const start = pos + 1;
          const end = pos + node.nodeSize - 1;

          editor.chain().focus().setTextSelection({ from: start, to: end }).setItalic().run();
        }
      });
    } else {
      const updatedDoc = editor.state.doc;

      updatedDoc.nodesBetween(from, to, (node, pos) => {
        if (node.isTextblock) {
          const start = pos + 1;
          const end = pos + node.nodeSize - 1;

          editor.chain().focus().setTextSelection({ from: start, to: end }).unsetItalic().run();
        }
      });
    }

    const fontName = fontNameWithsttyle
      ? fontNameWithsttyle.replace(/-(italic|normal)$/i, '').trim()
      : 'Lato';
    editor.chain().focus().setFontFamily(fontName).run();
    updateFontStyle(editor, isItalic ? 'italic' : 'normal');
    editor.chain().focus().setFontStyle(fontStyle).run();
    setEnableIcon(isItalic);
    editor.chain().focus().setTextSelection({ from, to }).run();
  };

  const getHeading = (level: Level | 0) => {
    if (level === 0 && editor.isActive('paragraph')) {
      return 'is-active';
    } else if (editor.isActive('heading', { level: level })) {
      return 'is-active';
    }
    return '';
  };
  return (
    <HeaderParagraphMenu
      placement='bottom-start'
      boundary={document.body}
      showCenter
      element={
        <EditorMenuButton data-testid='heading-btn'>
          <Tooltip direction='bottom' info='Heading'>
            <IconDiv>
              <HeadingButton />
            </IconDiv>
          </Tooltip>
        </EditorMenuButton>
      }
    >
      <HeadingParagraphButton
        className={getHeading(1)}
        onClick={() => setHeading(1)}
        data-testid='headingButton1'
      >
        <h1>Heading 1</h1>
      </HeadingParagraphButton>
      <HeadingParagraphButton
        className={getHeading(2)}
        onClick={() => setHeading(2)}
        data-testid='headingButton2'
      >
        <h2>Heading 2</h2>
      </HeadingParagraphButton>
      <HeadingParagraphButton
        className={getHeading(3)}
        onClick={() => setHeading(3)}
        data-testid='headingButton3'
      >
        <h3>Heading 3</h3>
      </HeadingParagraphButton>
      <HeadingParagraphButton
        className={getHeading(4)}
        onClick={() => setHeading(4)}
        data-testid='headingButton4'
      >
        <h4>Heading 4</h4>
      </HeadingParagraphButton>
      <HeadingParagraphButton
        className={getHeading(5)}
        onClick={() => setHeading(5)}
        data-testid='headingButton5'
      >
        <h5>Heading 5</h5>
      </HeadingParagraphButton>
      <HeadingParagraphButton
        className={getHeading(6)}
        onClick={() => setHeading(6)}
        data-testid='headingButton6'
      >
        <h6>Heading 6</h6>
      </HeadingParagraphButton>
      <HeadingParagraphButton
        className={getHeading(0)}
        onClick={() => setHeading(0)}
        data-testid='paragraphButton'
      >
        <p>Paragraph text</p>
      </HeadingParagraphButton>
    </HeaderParagraphMenu>
  );
}
